import { useState, useMemo } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import {
  useReceivedDocuments,
  useAcceptRejectDocuments,
  ReceivedDocumentFilters,
  ReceivedDocumentTable,
  AcceptRejectDialog,
  PendingBanner,
  UploadDialog,
} from "@/features/received-documents";
import type { ReceivedDocumentsFilters } from "@/features/received-documents/hooks/useReceivedDocuments";
import type { BatchAction } from "@/features/received-documents/hooks/useAcceptRejectDocuments";

export function RecepcionPage() {
  const [filters, setFilters] = useState<ReceivedDocumentsFilters>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<BatchAction>("accept");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const { documents, total, isLoading, error, refetch } = useReceivedDocuments({
    filters,
    limit: 50,
    offset: 0,
  });

  const { isProcessing, progress, processBatch, reset } =
    useAcceptRejectDocuments();

  // Count pending documents
  const pendingCount = useMemo(
    () => documents.filter((d) => d.acceptanceStatus === "pending").length,
    [documents],
  );

  // Get selected documents
  const selectedDocuments = useMemo(
    () =>
      documents.filter(
        (d) => selectedIds.has(d.id) && d.acceptanceStatus === "pending",
      ),
    [documents, selectedIds],
  );

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const handleDownloadXml = async (id: string) => {
    try {
      const blob = await apiFetch.blob(`/api/received-documents/${id}/xml`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dte-recibido-${id}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast.error("Error al descargar el XML");
    }
  };

  const handleAcceptSingle = (id: string) => {
    setSelectedIds(new Set([id]));
    setDialogAction("accept");
    setDialogOpen(true);
  };

  const handleRejectSingle = (id: string) => {
    setSelectedIds(new Set([id]));
    setDialogAction("reject");
    setDialogOpen(true);
  };

  const handleAcceptSelected = () => {
    setDialogAction("accept");
    setDialogOpen(true);
  };

  const handleRejectSelected = () => {
    setDialogAction("reject");
    setDialogOpen(true);
  };

  const handleDialogConfirm = async (reason?: string) => {
    const ids = selectedDocuments.map((d) => d.id);
    const folios = selectedDocuments.map((d) => d.folio);

    await processBatch(ids, folios, dialogAction, reason);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedIds(new Set());
    reset();
    // Refetch to update the list
    if (progress && progress.succeeded > 0) {
      refetch();
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-500">Error: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Recepción de Documentos</h1>
          <p className="text-muted-foreground">
            Gestiona los documentos tributarios recibidos de proveedores
          </p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Subir XML
        </Button>
      </div>

      {/* Filters */}
      <ReceivedDocumentFilters
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {/* Pending/Selection Banner */}
      <PendingBanner
        pendingCount={pendingCount}
        selectedCount={selectedDocuments.length}
        onAcceptSelected={handleAcceptSelected}
        onRejectSelected={handleRejectSelected}
      />

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">
          Cargando documentos...
        </div>
      ) : (
        <ReceivedDocumentTable
          documents={documents}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onDownloadXml={handleDownloadXml}
          onAccept={handleAcceptSingle}
          onReject={handleRejectSingle}
        />
      )}

      {/* Pagination info */}
      {!isLoading && documents.length > 0 && (
        <div className="mt-4 text-sm text-muted-foreground">
          Mostrando {documents.length} de {total} documentos
        </div>
      )}

      {/* Accept/Reject Dialog */}
      <AcceptRejectDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open && !isProcessing) {
            handleDialogClose();
          }
        }}
        documents={selectedDocuments}
        action={dialogAction}
        isProcessing={isProcessing}
        progress={progress}
        onConfirm={handleDialogConfirm}
        onClose={handleDialogClose}
      />

      {/* Upload Dialog */}
      <UploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onSuccess={refetch}
      />
    </div>
  );
}
